import {Grid} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/CloseOutlined';
import {ReactElement, useEffect, useMemo, useState} from 'react';
import {PAYMENT_CALLBACK_URL, PAYMENTS_URL} from '../../../shared/constants/api-urls';
import NxButton, {MaterialButtonVariant} from '../../shared/nxButton/NxButton';
import {TABLE_DATE_TIME_FORMAT} from '../../../shared/constants/format-templates';
import useGet from '../../../shared/hooks/use-get.hook';
import usePost from '../../../shared/hooks/use-post.hook';
import {getAmountOrNullReplacement, getFormattedDateOrNullReplacement, getValueOrNullReplacement} from '../../../shared/utils/valueFormatter';
import {TransHelper} from '../../../utils/trans-helper';
import NxLoader, {NxLoaderColorVariant, NxLoaderVariant} from '../../shared/nxLoader/NxLoader';
import PaymentStatusColored from '../../shared/paymentStatusColored/PaymentStatusColored';
import FullScreenPopup from '../../shared/full-screen-popup/FullScreenPopup';
import {
  AdditionalAttributes,
  fromPaymentCallbackDetailsTO,
  fromPaymentTo,
  GcashAttributesTransKeys,
  Payment,
  PaymentCallbackDetails,
  PaymentCallbackDetailsTO,
  PaymentTO,
} from '../Payment.model';
import styles from './PaymentDetails.module.scss';
import {SettlementInstitution} from '../../../shared/model/SettlementInstitution.model';
import axios from 'axios';

export const PrefixTrans = TransHelper.getPrefixedTrans('PAYMENTS');
export const SharedTrans = TransHelper.getPrefixedTrans('SHARED');

interface PaymentsDetailsProps {
  id: string;
  env: string;
  onClose: () => void;
  institutions?: SettlementInstitution[];
}

export default function PaymentDetails({id, env, institutions, onClose}: PaymentsDetailsProps): ReactElement {

  const [loading, setLoading] = useState<boolean>(true);
  const [retryPopupOpen, setRetryPopupOpen] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<Payment | undefined>(undefined);
  const [paymentCallbackDetails, setPaymentCallbackDetails] = useState<PaymentCallbackDetails | undefined>(undefined);
  const [paymentRetryingCallback, setPaymentRetryingCallback] = useState(false);
  const getPaymentDetails = useGet<PaymentTO>(`${PAYMENTS_URL}/${id}`);
  const getPaymentCallbackDetails = useGet<PaymentCallbackDetailsTO>(`${PAYMENT_CALLBACK_URL}/${id}`);
  const paymentRetryCallback = usePost<PaymentCallbackDetailsTO, void>(`${PAYMENT_CALLBACK_URL}/${id}/retry`);

  useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();
    getPaymentDetails(new URLSearchParams({env}), cancelTokenSource)
      .then((paymentTo: PaymentTO) => {
        const institutionName = institutions?.find((i) => i.code === paymentTo.institutionCode)?.name || '';
        setPaymentDetails(fromPaymentTo({...paymentTo, institutionName}));
        setLoading(false);
      });

    getPaymentCallbackDetails(null, cancelTokenSource)
      .then((response: PaymentCallbackDetailsTO) => {
        setPaymentCallbackDetails(fromPaymentCallbackDetailsTO(response));
      })
      .catch(() => {
        setPaymentCallbackDetails(undefined);
        setLoading(false);
      });
  }, []);

  const handleRetryConfirmation = (confirmed: boolean): void => {
    if (confirmed) {
      setPaymentRetryingCallback(true);
      paymentRetryCallback()
        .then((response: PaymentCallbackDetailsTO) => {
          setPaymentCallbackDetails(fromPaymentCallbackDetailsTO(response));
        })
        .catch(() => {
          setPaymentCallbackDetails(undefined);
        })
        .finally(() => {
          setPaymentRetryingCallback(false);
          setRetryPopupOpen(false);
        });
    } else {
      setRetryPopupOpen(false);
    }
  };

  const retryCallback = (): void => {
    setRetryPopupOpen(true);
  };

  const gcashAttributes = useMemo(() => {
    if (!paymentDetails) {
      return [];
    }

    const rows: string[][] = [];
    const attributes = Object.keys(paymentDetails.attributes || {})
      .filter((key) => !!GcashAttributesTransKeys[key]);

    if (paymentDetails?.[AdditionalAttributes.BANK_ACCOUNT_NO]) {
      attributes.unshift(AdditionalAttributes.BANK_ACCOUNT_NO);
    }

    if (paymentDetails?.[AdditionalAttributes.INSTITUTION_REF_NO]) {
      attributes.unshift(AdditionalAttributes.INSTITUTION_REF_NO);
    }

    if (paymentDetails?.[AdditionalAttributes.QRPH_INVOICE_NO]) {
      attributes.unshift(AdditionalAttributes.QRPH_INVOICE_NO);
    }

    if (!attributes.length) {
      return [];
    }

    for (let i = 0; i < attributes.length; i += 2) {
      const chunk = attributes.slice(i, i + 2);
      rows.push(chunk);
    }

    return rows;
  }, [paymentDetails]);

  return (
    <div>
      <Grid container className={styles.wrapper}>
        <div className={styles.title}>
          <PrefixTrans>DETAILS.TITLE</PrefixTrans>
          <IconButton
            color='inherit'
            aria-label='close details'
            onClick={onClose}
            edge='start'
            className={styles.title__close}>
            <CloseIcon />
          </IconButton>
        </div>
        <NxLoader variant={NxLoaderVariant.SMALL} colorVariant={NxLoaderColorVariant.TEXT} loaded={!loading}>
          <div className={styles.row}>
            <Grid xs={6} item>
              <div className={styles.label}>
                <PrefixTrans>PAYMENT_ID</PrefixTrans>
              </div>
              <div className={styles.value}>
                {paymentDetails?.id}
              </div>
            </Grid>
            <Grid xs={6} item>
              <div className={styles.label}>
                <PrefixTrans>REFERENCE_NO</PrefixTrans>
              </div>
              <div className={styles.value}>
                {paymentDetails?.referenceNo}
              </div>
            </Grid>
          </div>
          <div className={styles.row}>
            <Grid xs={6} item>
              <div className={styles.label}>
                <PrefixTrans>CREATED</PrefixTrans>
              </div>
              <div className={styles.value}>
                {paymentDetails?.createdOn.format(TABLE_DATE_TIME_FORMAT)}
              </div>
            </Grid>
            <Grid xs={6} item>
              <div className={styles.label}>
                <SharedTrans>AMOUNT</SharedTrans>
              </div>
              <div className={styles.value}>
                {getAmountOrNullReplacement(paymentDetails?.amount)}
              </div>
            </Grid>
          </div>
          {!!paymentDetails?.fee && (
            <div className={styles.row}>
              <Grid xs={6} item>
                <div className={styles.label}>
                  <PrefixTrans>FEE</PrefixTrans>
                </div>
                <div className={styles.value}>
                  {getAmountOrNullReplacement(paymentDetails?.fee)}
                </div>
              </Grid>
              <Grid xs={6} item>
                <div className={styles.label}>
                  <PrefixTrans>SUMMARY.TOTAL_AMOUNT</PrefixTrans>
                </div>
                <div className={styles.value}>
                  {getAmountOrNullReplacement(paymentDetails?.totalAmount)}
                </div>
              </Grid>
            </div>
          )}
          <div className={styles.row}>
            <Grid xs={6} item>
              <div className={styles.label}>
                <PrefixTrans>PAYMENT_STATUS</PrefixTrans>
              </div>
              <div className={styles.value}>
                {
                  paymentDetails?.paymentStatus && <PaymentStatusColored status={paymentDetails?.paymentStatus} />
                }
              </div>
            </Grid>
            <Grid xs={6} item>
              <div className={styles.label}>
                <PrefixTrans>EXECUTED</PrefixTrans>
              </div>
              <div className={styles.value}>
                {
                  paymentDetails?.paymentStatus === 'EXECUTED' ? getFormattedDateOrNullReplacement(paymentDetails.executedOn) : '-'
                }
              </div>
            </Grid>
          </div>
          <div className={styles.row}>
            <Grid xs={6} item>
              <div className={styles.label}>
                <SharedTrans>SOURCE_OF_FUNDS</SharedTrans>
              </div>
              <div className={styles.value}>
                {getValueOrNullReplacement(paymentDetails?.sourceOfFund)}
              </div>
            </Grid>
            <Grid xs={6} item>
              <div className={styles.label}>
                <PrefixTrans>CANCELED</PrefixTrans>
              </div>
              <div className={styles.value}>
                {getFormattedDateOrNullReplacement(paymentDetails?.canceledOn)}
              </div>
            </Grid>
          </div>
          {gcashAttributes.map((row, index) => (
            <div key={index} className={styles.row}>
              {row.map((col) => (
                <Grid key={col} xs={6} item>
                  <div className={styles.label}>
                    <PrefixTrans>{`DETAILS.${GcashAttributesTransKeys[col]}`}</PrefixTrans>
                  </div>
                  <div className={styles.value}>
                    {getValueOrNullReplacement(paymentDetails?.attributes?.[col] || paymentDetails?.[col])}
                  </div>
                </Grid>
              ))}
            </div>
          ))}
          <div className={styles.row}>
            <Grid xs={6} item>
              <div className={styles.label}>
                <PrefixTrans>REJECTION_REASON</PrefixTrans>
              </div>
              <div className={styles.value}>
                {paymentDetails?.rejectionReason ? paymentDetails.rejectionReason : 'N/A'}
              </div>
            </Grid>
            <Grid xs={6} item>
              <div className={styles.label}>
                <PrefixTrans>REJECTED_ON</PrefixTrans>
              </div>
              <div className={styles.value}>
                {
                  paymentDetails?.paymentStatus === 'REJECTED' ? getFormattedDateOrNullReplacement(paymentDetails.executedOn) : '-'
                }
              </div>
            </Grid>
          </div>
          <div className={styles.row}>
            <Grid xs={6} item>
              <div className={styles.label}>
                <PrefixTrans>EXPIRED_ON</PrefixTrans>
              </div>
              <div className={styles.value}>
                {
                  paymentDetails?.paymentStatus === 'EXPIRED' ? getFormattedDateOrNullReplacement(paymentDetails.expiredOn) : '-'
                }
              </div>
            </Grid>
          </div>
        </NxLoader>
        <div className={styles.callback_wrapper}>
          <div className={styles.callback_title}>
            <PrefixTrans>CALLBACK.TITLE</PrefixTrans>
          </div>
          {paymentCallbackDetails ? (
            <>
              <div className={styles.row}>
                <Grid xs={6} item>
                  <div className={styles.label}>
                    <PrefixTrans>CALLBACK.STATUS</PrefixTrans>
                  </div>
                  <div className={styles.value}>
                    {
                      paymentCallbackDetails?.success
                        ? <PrefixTrans>CALLBACK.SUCCESS</PrefixTrans>
                        : <PrefixTrans>CALLBACK.ERROR</PrefixTrans>
                    }
                  </div>
                </Grid>
                <Grid xs={6} item>
                  <div className={styles.label}>
                    <PrefixTrans>CALLBACK.EXECUTED_ON</PrefixTrans>
                  </div>
                  <div className={styles.value}>
                    {paymentCallbackDetails.timestamp.format(TABLE_DATE_TIME_FORMAT)}
                  </div>
                </Grid>
              </div>
              <div className={styles.row}>
                <Grid xs={6} item>
                  <div className={styles.label}>
                    <PrefixTrans>CALLBACK.RESPONSE_CODE</PrefixTrans>
                  </div>
                  <div className={styles.value}>
                    {paymentCallbackDetails.responseCode}
                  </div>
                </Grid>
                <Grid xs={6} item>
                  <div className={styles.label}>
                    <PrefixTrans>CALLBACK.URL</PrefixTrans>
                  </div>
                  <div className={styles.value}>
                    {paymentCallbackDetails.payload?.callbackUrl}
                  </div>
                </Grid>
              </div>
              <div className={styles.row}>
                {paymentCallbackDetails.errorMessage &&
                  <Grid xs={6} item>
                    <div className={styles.label}>
                      <PrefixTrans>CALLBACK.ERROR_MESSAGE</PrefixTrans>
                    </div>
                    <div className={styles.value}>
                      {paymentCallbackDetails.errorMessage}
                    </div>
                  </Grid>
                }
              </div>
              <div className={styles.row}>
                <NxButton onClick={retryCallback} >
                  <SharedTrans>RETRY</SharedTrans>
                </NxButton>
              </div>
            </>
          ) : (
            <div className={styles.label}>
              <PrefixTrans>CALLBACK.DATA_NOT_AVAILABLE</PrefixTrans>
            </div>
          )}
        </div>
        <FullScreenPopup
          header="Retry Confirmation"
          popupState={{open: retryPopupOpen, setOpen: setRetryPopupOpen}}
        >
          <div>
            <p>Do you really want to resend the callback?</p>
            <div className={styles.button_wrapper}>
              <NxButton
                onClick={(): void => handleRetryConfirmation(true)}
                className={styles.button}
                disabled={paymentRetryingCallback} >
                <NxLoader variant={NxLoaderVariant.SMALL} loaded={!paymentRetryingCallback}>
                  <SharedTrans>YES</SharedTrans>
                </NxLoader>
              </NxButton>
              <NxButton
                onClick={(): void => handleRetryConfirmation(false)}
                variant={MaterialButtonVariant.OUTLINED} >
                <SharedTrans>NO</SharedTrans>
              </NxButton>
            </div>
          </div>
        </FullScreenPopup>
      </Grid>
    </div>
  );
}
